/**
 * ForgotPassword Component
 *
 * Password reset request page.
 * Integrates with @cfleet/api-client useRequestPasswordReset hook for backend API calls.
 *
 * Flow:
 * 1. User enters their email address
 * 2. Form submits to POST /auth/password/reset via useRequestPasswordReset hook
 * 3. On success, show "check your email" message
 * 4. User clicks link in email and goes to /reset-password with token
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRequestPasswordReset } from '@cfleet/api-client';
import {
  ArrowRight,
  ArrowLeft,
  Mail,
  ShieldCheck,
  AlertCircle,
  CheckCircle2,
  Loader2,
} from 'lucide-react';

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const { mutate: requestReset, isPending } = useRequestPasswordReset();

  // Form state
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    requestReset(
      { email },
      {
        onSuccess: () => {
          setSuccess(true);
        },
        onError: (err) => {
          setError(err instanceof Error ? err.message : 'An error occurred');
        },
      }
    );
  };

  const handleBackToSignIn = () => {
    navigate('/login');
  };

  // Clear error when user starts typing
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (error) setError(null);
    setEmail(e.target.value);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4 font-sans text-slate-900 relative overflow-hidden">
      {/* Background Decor */}
      <div className="absolute top-0 left-0 w-full h-96 bg-gradient-to-b from-cfleet-100/50 to-transparent -z-10"></div>

      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden animate-in fade-in zoom-in-95 duration-500">
        {!success ? (
          <div className="p-8">
            {/* Logo */}
            <div className="flex justify-center mb-8">
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 bg-cfleet-600 rounded-lg flex items-center justify-center text-white text-sm font-bold shadow-lg shadow-cfleet-500/30">
                  C
                </div>
                <span className="font-bold text-3xl text-slate-800 tracking-tight">cfleet</span>
              </div>
            </div>

            <div className="mb-8 text-center">
              <h2 className="text-xl font-bold text-slate-900">Forgot your password?</h2>
              <p className="text-sm text-slate-500 mt-2">
                Enter your email address and we'll send you a link to reset your password.
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-3 bg-rose-50 border border-rose-200 rounded-lg flex items-start gap-2 animate-in fade-in slide-in-from-top-2">
                <AlertCircle size={18} className="text-rose-500 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-rose-700">{error}</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label
                  htmlFor="email"
                  className="block text-xs font-bold text-slate-700 uppercase mb-1.5 tracking-wide"
                >
                  Email Address
                </label>
                <div className="relative group">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-cfleet-500 transition-colors">
                    <Mail size={18} />
                  </div>
                  <input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={handleEmailChange}
                    disabled={isPending}
                    className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cfleet-500/20 focus:border-cfleet-500 transition-all placeholder:text-slate-400 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="name@company.com"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isPending}
                className="w-full bg-cfleet-600 text-white py-3 rounded-lg text-sm font-bold shadow-lg shadow-cfleet-500/30 hover:bg-cfleet-700 hover:shadow-xl hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2 mt-6 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none disabled:hover:shadow-lg"
              >
                {isPending ? (
                  <Loader2 size={20} className="animate-spin" />
                ) : (
                  <>
                    Send Reset Link <ArrowRight size={18} />
                  </>
                )}
              </button>
            </form>
          </div>
        ) : (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 size={32} />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Check your email</h2>
            <p className="text-sm text-slate-500 mb-2">
              We've sent a password reset link to:
            </p>
            <p className="text-sm font-semibold text-slate-800 mb-6">{email}</p>
            <p className="text-xs text-slate-400 mb-6">
              If you don't see the email, check your spam folder.
            </p>
          </div>
        )}

        <div className="px-8 py-5 bg-slate-50 border-t border-slate-100 text-center">
          <button
            onClick={handleBackToSignIn}
            className="inline-flex items-center gap-1.5 text-xs text-slate-600 font-bold hover:text-cfleet-600 transition-colors"
          >
            <ArrowLeft size={14} /> Back to Sign In
          </button>
        </div>
      </div>

      {/* Footer Info */}
      <div className="mt-8 text-center space-y-3">
        <div className="flex items-center justify-center gap-2 text-xs text-emerald-600 font-medium bg-emerald-50 px-3 py-1 rounded-full w-fit mx-auto border border-emerald-100">
          <ShieldCheck size={12} /> Secure Connection
        </div>
        <p className="text-xs text-slate-400">© 2025 cfleet. All rights reserved.</p>
      </div>
    </div>
  );
};

export default ForgotPassword;
